import * as React from 'react';
import dateUtil from '../../../utils/dateUtil';
import { Employee } from '../../../hooks/useEmployeeData';
import TimeTrackingService from '../services/TimeTrackingService';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import timeTrackingErrorHandler from '../services/TimeTrackingErrorHandler';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import { toast } from 'react-toastify';

interface WorkArea {
  id: number;
  name: string;
  [key: string]: any;
}

interface TimeTrackingEditFormData {
  id: number | null;
  employee: string;
  work_area: string;
  date: string;
  start_time: string;
  end_time: string;
  break_start: string;
  break_end: string;
}

interface TTEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editData: TimeTrackingEditFormData;
  setEditData: React.Dispatch<React.SetStateAction<TimeTrackingEditFormData>>;
  employees: Employee[];
  workAreas: WorkArea[];
}

const addOneHour = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export default function TTEditModal({
  open,
  onClose,
  onSave,
  editData,
  setEditData,
  employees,
  workAreas,
}: TTEditModalProps): React.JSX.Element | null {
  if (!open) return null;

  const handleChange= (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value }= e.target;
    const update: any = { [name]: value };
    if (name === 'start_time' && value) update.end_time = addOneHour(value);
    if (name === 'break_start' && value) update.break_end = addOneHour(value);
    setEditData(prev => ({...prev, ...update}));
  };

  const handleUpdate= async () => {
    if (!editData.date || !editData.start_time || !editData.end_time) {
      toast.warning('Please fill out date & times');
      return;
    }
    const stIso= dateUtil.createTenantTimeAsUTC(editData.date, editData.start_time);
    const etIso= dateUtil.createTenantTimeAsUTC(editData.date, editData.end_time);

    let bsIso=null, beIso=null;
    if (editData.break_start) bsIso= dateUtil.createTenantTimeAsUTC(editData.date, editData.break_start);
    if (editData.break_end)   beIso= dateUtil.createTenantTimeAsUTC(editData.date, editData.break_end);

    const payload={
      employee: editData.employee,
      work_area: editData.work_area,
      start_time: stIso,
      end_time:   etIso,
      break_start: bsIso,
      break_end:   beIso
    };

    try {
      await TimeTrackingService.updateTimeTracking(editData.id!, payload);
      onClose();
      onSave();
    } catch (error) {
      console.error('Edit TT =>', error);

      if (error instanceof HttpError) {
        if (!timeTrackingErrorHandler.handle(error)) {
          globalErrorHandler.handle(error);
        }
      } else {
        toast.error('Fehler beim Aktualisieren der Zeiterfassung');
      }
    }
  };

  const fieldLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };
  const fieldInput: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' };
  const fieldRow: React.CSSProperties = { marginBottom: 12 };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 400, maxWidth: '92vw', padding: 24,
          background: '#fff', borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)',
          animation: 'fadeIn 0.15s ease-out',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.navy }}>Zeiterfassung bearbeiten</h3>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: colors.textLight, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8 }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.color = colors.textBody; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = colors.textLight; }}
          >{'×'}</button>
        </div>

        {/* Employee & Work Area */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>Mitarbeiter</div>
            <select name="employee" value={editData.employee} onChange={handleChange} style={fieldInput}>
              <option value="">(Auswahl)</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>Work Area</div>
            <select name="work_area" value={editData.work_area} onChange={handleChange} style={fieldInput}>
              <option value="">(keine)</option>
              {workAreas.map(wa => (
                <option key={wa.id} value={wa.id}>{wa.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Date */}
        <div style={fieldRow}>
          <div style={fieldLabel}>Datum</div>
          <input type="date" name="date" value={editData.date} onChange={handleChange} style={fieldInput} />
        </div>

        {/* Start & End */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>Start</div>
            <input type="time" name="start_time" value={editData.start_time} onChange={handleChange} style={fieldInput} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>Ende</div>
            <input type="time" name="end_time" value={editData.end_time} onChange={handleChange} style={fieldInput} data-testid="time-tracking-end-time-input" />
          </div>
        </div>

        {/* Break */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>Pause Start</div>
            <input type="time" name="break_start" value={editData.break_start} onChange={handleChange} style={fieldInput} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>Pause Ende</div>
            <input type="time" name="break_end" value={editData.break_end} onChange={handleChange} style={fieldInput} />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${colors.border}`, background: '#fff', color: colors.textMuted, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleUpdate}
            data-testid="save-time-tracking-button"
            style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: colors.navy, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
